import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Text, Slider, SliderTrack,
  SliderFilledTrack, SliderThumb, Box, useToast, Badge, Flex, Input, FormControl, FormLabel,
  Divider, Select, Switch,
} from '@chakra-ui/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

import { SOUND_OPTIONS } from './AlarmPopup';

const PRESETS = [
  { label: 'Compact', value: 0.85 },
  { label: 'Default', value: 1.0 },
  { label: 'Large', value: 1.15 },
  { label: 'Extra Large', value: 1.3 },
];

const COLOR_PRESETS = [
  { label: 'Classic', row1: '#FFFFFF', row2: '#F7FAFC', highlight: '#EE7203' },
  { label: 'Cool Grey', row1: '#FFFFFF', row2: '#EDF2F7', highlight: '#3182CE' },
  { label: 'Warm', row1: '#FFFAF0', row2: '#FFF5F5', highlight: '#DD6B20' },
  { label: 'Dark Mode', row1: '#2D3748', row2: '#1A202C', highlight: '#ED8936' },
];

export default function AccessibilityModal({ isOpen, onClose, currentScale, onScaleChange }) {
  const { user } = useAuth();
  const [scale, setScale] = useState(currentScale || 1.0);
  const [savedScale, setSavedScale] = useState(currentScale || 1.0);
  const [rowColor1, setRowColor1] = useState('#FFFFFF');
  const [rowColor2, setRowColor2] = useState('#F7FAFC');
  const [highlightColor, setHighlightColor] = useState('#EE7203');
  const [dimEmptyIconsColor, setDimEmptyIconsColor] = useState('#CBD5E0');
  const [weekStartDay, setWeekStartDay] = useState(1); // 0=Sun,1=Mon,6=Sat
  const [alarmSound, setAlarmSound] = useState('single_beep');
  const [showLogos, setShowLogos] = useState(true);
  const [showAvatars, setShowAvatars] = useState(true);
  const [logoSize, setLogoSize] = useState(20);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      const s = currentScale || 1.0;
      setScale(s);
      setSavedScale(s);
      setRowColor1(user?.row_color_1 || '#FFFFFF');
      setRowColor2(user?.row_color_2 || '#F7FAFC');
      setHighlightColor(user?.highlight_color || '#EE7203');
      setDimEmptyIconsColor(user?.dim_empty_icons_color || '#CBD5E0');
      setWeekStartDay(user?.week_start_day !== undefined ? user.week_start_day : 1);
      setAlarmSound(user?.alarm_sound || 'single_beep');
      setShowLogos(user?.show_logos !== false);
      setShowAvatars(user?.show_avatars !== false);
      setLogoSize(user?.logo_size || 20);
    }
  }, [isOpen, currentScale, user]);

  // Apply zoom only when slider is released so page doesn't jump while dragging
  const handleScaleChange = (v) => {
    setScale(v / 100);
  };
  const handleScaleChangeEnd = (v) => {
    const newScale = v / 100;
    setScale(newScale);
    document.documentElement.style.zoom = newScale !== 1.0 ? newScale : '';
  };

  // Revert zoom if closed without saving
  const handleClose = () => {
    document.documentElement.style.zoom = savedScale !== 1.0 ? savedScale : '';
    onClose();
  };

  const handleSave = async () => {
    try {
      await api.put('/auth/preferences', {
        text_scale: scale,
        row_color_1: rowColor1,
        row_color_2: rowColor2,
        highlight_color: highlightColor,
        dim_empty_icons_color: dimEmptyIconsColor,
        week_start_day: weekStartDay,
        alarm_sound: alarmSound,
        show_logos: showLogos,
        show_avatars: showAvatars,
        logo_size: logoSize,
      });
      onScaleChange(scale);
      // Update user in localStorage so MainGrid picks up changes immediately
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          const u = JSON.parse(stored);
          u.text_scale = scale; u.row_color_1 = rowColor1; u.row_color_2 = rowColor2; u.highlight_color = highlightColor; u.dim_empty_icons_color = dimEmptyIconsColor; u.week_start_day = weekStartDay; u.alarm_sound = alarmSound; u.show_logos = showLogos; u.show_avatars = showAvatars; u.logo_size = logoSize;
          localStorage.setItem('user', JSON.stringify(u));
        } catch(e) {}
      }
      toast({ title: 'Settings saved — refresh to apply colours', status: 'success', duration: 2000 });
      onClose();
      window.location.reload();
    } catch (e) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    }
  };

  const applyPreset = (p) => { setRowColor1(p.row1); setRowColor2(p.row2); setHighlightColor(p.highlight); };

  const pct = Math.round(scale * 100);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader fontSize="md">Accessibility</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Box w="100%">
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" fontWeight="600">Text Size</Text>
                <Badge colorScheme="blue" fontSize="11px">{pct}%</Badge>
              </Flex>
              <Slider min={70} max={150} step={5} value={pct}
                onChange={handleScaleChange}
                onChangeEnd={handleScaleChangeEnd}
                colorScheme="blue">
                <SliderTrack h="8px" borderRadius="full">
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={5} />
              </Slider>
              <Flex justify="space-between" mt={1}>
                <Text fontSize="9px" color="gray.400">70%</Text>
                <Text fontSize="9px" color="gray.400">100%</Text>
                <Text fontSize="9px" color="gray.400">150%</Text>
              </Flex>
            </Box>

            <HStack spacing={2} w="100%" justify="center">
              {PRESETS.map(p => (
                <Button key={p.value} size="xs" variant={scale === p.value ? 'solid' : 'outline'}
                  colorScheme={scale === p.value ? 'blue' : 'gray'}
                  onClick={() => handleScaleChangeEnd(Math.round(p.value * 100))}>
                  {p.label}
                </Button>
              ))}
            </HStack>

            <Divider />

            <Box w="100%">
              <Text fontSize="xs" fontWeight="600" mb={2}>Row Colours</Text>
              <HStack spacing={3} mb={2}>
                <FormControl>
                  <FormLabel fontSize="10px" mb={0.5}>Row 1 (Even)</FormLabel>
                  <HStack>
                    <Input size="sm" type="color" value={rowColor1} onChange={e => setRowColor1(e.target.value)} w="40px" h="30px" p={0} border="none" />
                    <Input size="xs" value={rowColor1} onChange={e => setRowColor1(e.target.value)} w="80px" fontFamily="mono" fontSize="10px" />
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="10px" mb={0.5}>Row 2 (Odd)</FormLabel>
                  <HStack>
                    <Input size="sm" type="color" value={rowColor2} onChange={e => setRowColor2(e.target.value)} w="40px" h="30px" p={0} border="none" />
                    <Input size="xs" value={rowColor2} onChange={e => setRowColor2(e.target.value)} w="80px" fontFamily="mono" fontSize="10px" />
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="10px" mb={0.5}>Highlight</FormLabel>
                  <HStack>
                    <Input size="sm" type="color" value={highlightColor} onChange={e => setHighlightColor(e.target.value)} w="40px" h="30px" p={0} border="none" />
                    <Input size="xs" value={highlightColor} onChange={e => setHighlightColor(e.target.value)} w="80px" fontFamily="mono" fontSize="10px" />
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="10px" mb={0.5}>Empty Icon Dim</FormLabel>
                  <HStack>
                    <Input size="sm" type="color" value={dimEmptyIconsColor} onChange={e => setDimEmptyIconsColor(e.target.value)} w="40px" h="30px" p={0} border="none" />
                    <Input size="xs" value={dimEmptyIconsColor} onChange={e => setDimEmptyIconsColor(e.target.value)} w="80px" fontFamily="mono" fontSize="10px" />
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="10px" mb={0.5}>Week Starts On</FormLabel>
                  <Select size="xs" value={weekStartDay} onChange={e => setWeekStartDay(Number(e.target.value))} w="140px">
                    <option value={1}>Monday</option>
                    <option value={0}>Sunday</option>
                    <option value={6}>Saturday</option>
                  </Select>
                </FormControl>
              </HStack>
              <HStack spacing={1}>
                {COLOR_PRESETS.map(p => (
                  <Button key={p.label} size="xs" variant="outline" onClick={() => applyPreset(p)}>
                    <HStack spacing={1}>
                      <Box w="10px" h="10px" bg={p.row1} border="1px solid" borderColor="gray.300" borderRadius="sm" />
                      <Box w="10px" h="10px" bg={p.row2} border="1px solid" borderColor="gray.300" borderRadius="sm" />
                      <Box w="10px" h="10px" bg={p.highlight} borderRadius="sm" />
                      <Text fontSize="9px">{p.label}</Text>
                    </HStack>
                  </Button>
                ))}
              </HStack>
            </Box>

            <Divider />

            <Box w="100%">
              <Text fontSize="xs" fontWeight="600" mb={2}>Alarm Sound</Text>
              <Select size="sm" value={alarmSound} onChange={e => setAlarmSound(e.target.value)}>
                {SOUND_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Select>
            </Box>

            <Divider />

            <Box w="100%">
              <Text fontSize="xs" fontWeight="600" mb={2}>Display</Text>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="12px">Show Company Logos</Text>
                <Switch size="sm" colorScheme="blue" isChecked={showLogos} onChange={e => setShowLogos(e.target.checked)} />
              </Flex>
              <Flex justify="space-between" align="center">
                <Text fontSize="12px">Show Avatars</Text>
                <Switch size="sm" colorScheme="purple" isChecked={showAvatars} onChange={e => setShowAvatars(e.target.checked)} />
              </Flex>
              {showLogos && (
                <Box mt={2}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="11px" color="gray.600">Logo / Avatar Size</Text>
                    <Badge colorScheme="blue" fontSize="10px">{logoSize}px</Badge>
                  </Flex>
                  <Slider min={12} max={48} step={2} value={logoSize}
                    onChange={(v) => setLogoSize(v)} colorScheme="blue">
                    <SliderTrack h="6px" borderRadius="full">
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                  <Flex justify="space-between" mt={0.5}>
                    <Text fontSize="8px" color="gray.400">12px</Text>
                    <Text fontSize="8px" color="gray.400">20px</Text>
                    <Text fontSize="8px" color="gray.400">48px</Text>
                  </Flex>
                </Box>
              )}
            </Box>

            <Box w="100%" p={3} border="1px" borderColor="gray.200" borderRadius="md">
              <Text fontSize="xs" fontWeight="600" mb={2} color="gray.500">Preview</Text>
              <Box style={{ fontSize: `${scale}em` }}>
                <Box bg={rowColor1} p={1} borderRadius="sm" mb={0.5}>
                  <Text fontWeight="600">Even Row — Sample Project</Text>
                </Box>
                <Box bg={rowColor2} p={1} borderRadius="sm" mb={0.5}>
                  <Text fontWeight="600">Odd Row — Another Project</Text>
                </Box>
                <Box bg={highlightColor} color="white" p={1} borderRadius="sm">
                  <Text fontWeight="600">Hovered Row</Text>
                </Box>
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={() => { handleScaleChangeEnd(100); applyPreset(COLOR_PRESETS[0]); setLogoSize(20); }}>Reset</Button>
          <Button size="sm" variant="ghost" mr={2} onClick={handleClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
